import type { Contract, Milestone, ProjectFundingContract } from '../types';
import { effectiveTimelineDeliveryStatus } from './timelineDeliveryPresentation';

const isSigned = (contract: Contract) => contract.signingStatus === 'signed' ||
  contract.draftStatus === 'signed_by_both' || (contract.companySigned && contract.personSigned);

/** A different contract's unpaid notice must not block an already funded contract. */
export function contractFundingConfirmed(
  contract: Contract,
  funding?: ProjectFundingContract,
  legacyProjectFunded = false
): boolean {
  if (funding) return funding.contractId === contract.id && funding.signed &&
    Number.isFinite(funding.remainingFundingNeedOre) && funding.remainingFundingNeedOre <= 0;
  return legacyProjectFunded;
}

/** Funding is contract-wide, not an allocation of the shortfall to particular stages. */
export function timelineFundingNeedOre(contract: Contract, funding?: ProjectFundingContract): number {
  if (!isSigned(contract)) return 0;
  if (funding?.contractId === contract.id && Number.isFinite(funding.remainingFundingNeedOre)) {
    return Math.max(0, funding.remainingFundingNeedOre);
  }
  // Home/provider views do not fetch the funding projection. Contract amounts are
  // in kronor and include carried funding when a replacement awaits activation.
  const afterRot = Number(contract.afterRotAmount);
  const paid = Number(contract.paidAmount);
  const calculated = Number.isFinite(afterRot) && Number.isFinite(paid)
    ? Math.round((afterRot - paid) * 100) : 0;
  const remaining = Number(contract.remainingAmount);
  return Math.max(0, calculated, Number.isFinite(remaining) ? Math.round(remaining * 100) : 0);
}

export function timelineFundingMilestoneIds(milestones: Milestone[], fundingNeedOre: number): Set<string> {
  if (fundingNeedOre <= 0) return new Set();
  const remaining = milestones.filter(milestone =>
    milestone.deliveryStatus !== 'paid' && !milestone.paidAt &&
    (milestone.afterRotAmount ?? milestone.amount) > 0
  );
  const notStarted = remaining.filter(milestone =>
    !milestone.workStartedAt &&
    ['not_started', undefined].includes(effectiveTimelineDeliveryStatus(milestone))
  );
  // While future stages exist, leave ongoing work alone. In the final batch all
  // unpaid stages carry the warning, alongside (not instead of) their work status.
  return new Set((notStarted.length ? notStarted : remaining).map(milestone => milestone.id));
}

/** Display version only: never count it as a second operational agreement. */
export function pendingTimelineReplacement(contract: Contract, contracts: Contract[]): Contract | undefined {
  return contracts.filter(candidate =>
    candidate.projectId === contract.projectId &&
    candidate.replacesContractId === contract.id &&
    candidate.versionStatus === 'settlement_pending' &&
    isSigned(candidate)
  ).sort((left, right) => (right.contractVersion ?? right.contractNumber) -
    (left.contractVersion ?? left.contractNumber))[0];
}
